import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useImageUrl } from '@/hooks/useImageUrl';

interface ProfilePhotosGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  photos: string[];
  username: string;
}

const ProfilePhotosGallery: React.FC<ProfilePhotosGalleryProps> = ({
  isOpen,
  onClose,
  photos,
  username
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Get async URLs for current photo (zoom and thumbnail variants)
  const { url: zoomUrl } = useImageUrl(photos[currentIndex], 'zoom');
  const { url: thumbUrl } = useImageUrl(photos[currentIndex], 'thumbnail');

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const goToIndex = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] p-0 bg-black/95 border-white/10">
        <DialogDescription className="sr-only">
          Visualize todas as fotos do perfil de {username}
        </DialogDescription>
        <div className="relative flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div>
              <h3 className="text-white font-semibold">Fotos do Perfil</h3>
              <p className="text-white/60 text-sm">
                @{username} • {currentIndex + 1} de {photos.length}
              </p>
            </div>
            <DialogClose className="text-white hover:text-white/70 transition-colors">
              <X className="h-5 w-5" />
            </DialogClose>
          </div>

          {/* Main Image */}
          <div className="flex-1 flex items-center justify-center relative p-4">
            {zoomUrl && (
              <img
                src={zoomUrl}
                alt={`Foto ${currentIndex + 1}`}
                className="max-w-full max-h-[60vh] object-contain rounded"
              />
            )}

            {/* Navigation Arrows */}
            {photos.length > 1 && (
              <>
                <Button
                  onClick={goToPrevious}
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  onClick={goToNext}
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {photos.length > 1 && (
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2 overflow-x-auto scrollbar-none pb-2">
                {photos.map((photo, index) => (
                  <ThumbnailButton
                    key={index}
                    photo={photo}
                    index={index}
                    isActive={index === currentIndex}
                    onClick={() => goToIndex(index)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper component for thumbnail buttons with async URL loading
const ThumbnailButton: React.FC<{
  photo: string;
  index: number;
  isActive: boolean;
  onClick: () => void;
}> = ({ photo, index, isActive, onClick }) => {
  const { url: thumbnailUrl } = useImageUrl(photo, 'thumbnail');
  
  if (!thumbnailUrl) return null;
  
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
        isActive
          ? 'border-luxury-gold scale-105'
          : 'border-white/20 hover:border-white/40 opacity-70 hover:opacity-100'
      }`}
    >
      <img
        src={thumbnailUrl}
        alt={`Miniatura ${index + 1}`}
        className="w-full h-full object-cover"
      />
    </button>
  );
};

export default ProfilePhotosGallery;
